/**
 * Mobile-shell detection and the small amount of state the page keeps for
 * the shell (ERIN-8561). Kept free of heavy imports so phone.js and tests can
 * use it.
 *
 * Three wrappers host this app:
 *  - the React Native WebView shell, which injects `window.ERINShell`;
 *  - the Capacitor iOS shell (erin-engage/mobile), which appends
 *    `ErinMobileShell/<version>` to the user agent;
 *  - the Android Trusted Web Activity, which is plain Chrome and can only
 *    mark itself through the start URL (`?shell=1`). That mark is kept in
 *    sessionStorage, which survives the sign-out's localStorage.clear().
 *
 * The shell may also pass a stable per-install device id (`?d=`) that the
 * eAI portal binds its session to; it is carried over on the hand-off.
 */
export const SHELL_UA = /ErinMobileShell/;
const SESSION_FLAG = 'erin-shell';
const SESSION_DEVICE = 'erin-shell-device';
const SESSION_PORTAL = 'erin-shell-portal';
const HOME_KEY = 'erinShellHome';

const session = () => {
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
};

const local = () => {
	try {
		return window.localStorage;
	} catch {
		return null;
	}
};

/** Reads `?shell=1` / `?d=` off the URL once; call at startup. */
export const captureShellParams = (search = window.location.search) => {
	const params = new URLSearchParams(search);
	if (params.get('shell') === '1') session()?.setItem(SESSION_FLAG, '1');
	const device = params.get('d');
	if (device && /^[A-Za-z0-9-]{8,64}$/.test(device)) {
		session()?.setItem(SESSION_DEVICE, device);
	}
	// The shell's default eAI portal (`?p=`): where the identifier-first
	// login sends people this app does not know (ERIN-8561). https only,
	// bar a developer's local portal.
	const portal = params.get('p');
	if (
		portal &&
		/^(https:\/\/[^\s/]+|http:\/\/(localhost|127\.0\.0\.1)(:\d+)?)\/employee-portal\/?$/i.test(
			portal
		)
	) {
		session()?.setItem(SESSION_PORTAL, portal);
	}
};

/** The shell's default eAI portal URL, if it sent one. */
export const getShellPortalUrl = () =>
	session()?.getItem(SESSION_PORTAL) || null;

export const isMobileShell = () =>
	typeof window !== 'undefined' &&
	(Boolean(window.ERINShell) ||
		SHELL_UA.test(window.navigator?.userAgent || '') ||
		session()?.getItem(SESSION_FLAG) === '1');

/** The Capacitor shell: a WebView we cannot message, but window.open reaches Safari. */
export const isCapacitorShell = () =>
	typeof window !== 'undefined' &&
	!window.ERINShell &&
	SHELL_UA.test(window.navigator?.userAgent || '');

/** Per-install device id handed in by the shell, if any. */
export const getShellDeviceId = () =>
	window.ERINShell?.deviceId || session()?.getItem(SESSION_DEVICE) || null;

/**
 * Appends what the portal can use on arrival: the shell's device id (`?d=`,
 * the portal binds its session to it) and, on a hand-off, the signed-in
 * user's email (`?email=`, so the portal's installed-app login continues
 * straight to the PIN step instead of asking for it again).
 */
export const withShellParams = (
	url,
	{ deviceId = getShellDeviceId(), email, phone } = {}
) => {
	try {
		const u = new URL(url);
		if (deviceId && !u.searchParams.has('d')) u.searchParams.set('d', deviceId);
		if (
			email &&
			/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
			!u.searchParams.has('email')
		) {
			u.searchParams.set('email', email);
		}
		// A phone number from the identifier-first login; the portal signs
		// people in by text message.
		if (
			phone &&
			/^\d{10,15}$/.test(phone) &&
			!u.searchParams.has('identifier')
		) {
			u.searchParams.set('identifier', phone);
		}
		return u.toString();
	} catch {
		return url;
	}
};

/**
 * The portal an install was handed to. Lives in localStorage of the legacy
 * origin so the next launch of a shell that has no memory of its own (the
 * Capacitor shell, the TWA) goes straight back to the portal. Written after
 * the sign-out has cleared storage; the RN shell keeps its own copy too.
 */
const PORTAL_URL = /^(https:\/\/|http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/)/i;

export const rememberShellHome = ({ url, companyId, email }) => {
	if (!PORTAL_URL.test(url || '')) return;
	try {
		local()?.setItem(
			HOME_KEY,
			JSON.stringify({
				url,
				companyId: companyId || null,
				// Used by the very next portal navigation only (see takeShellHandoffEmail).
				email: email || null,
			})
		);
	} catch {
		// storage unavailable; the hand-off still navigates
	}
};

/**
 * The email of the user who was just handed off, read once. Both the logout
 * page and the API layer's pre-login routing may navigate to the portal
 * during a hand-off; whichever wins carries it, and it is not kept around
 * for later launches.
 */
export const takeShellHandoffEmail = () => {
	try {
		const raw = local()?.getItem(HOME_KEY);
		if (!raw) return null;
		const home = JSON.parse(raw);
		if (!home?.email) return null;
		local()?.setItem(HOME_KEY, JSON.stringify({ ...home, email: null }));
		return home.email;
	} catch {
		return null;
	}
};

export const getRememberedShellHome = () => {
	try {
		const raw = local()?.getItem(HOME_KEY);
		if (!raw) return null;
		const home = JSON.parse(raw);
		return PORTAL_URL.test(home?.url || '') ? home : null;
	} catch {
		return null;
	}
};

export const forgetShellHome = () => {
	try {
		local()?.removeItem(HOME_KEY);
	} catch {
		// nothing to forget
	}
};

/**
 * True while the logout page is moving this install to the portal. Set as a
 * class on <html> so stylesheets can hide late toasts; checked by the API
 * layer so its own pre-login routing does not navigate a second time (the
 * later navigation would win, without the hand-off email).
 */
export const HANDING_OFF_CLASS = 'erin-handing-off';

export const beginShellHandoff = () =>
	document.documentElement.classList.add(HANDING_OFF_CLASS);

export const isHandingOff = () =>
	typeof document !== 'undefined' &&
	document.documentElement.classList.contains(HANDING_OFF_CLASS);

/**
 * The SSO callback reached outside any shell with the shell's tag: a sign-in
 * the shell started finished in the system browser (Apple's sign-in sheet or
 * Safari). Hand the result back to the app on erinapp:// before the app even
 * boots: that browser context may not offer what the app needs to start
 * (IndexedDB in the sign-in sheet, for one), and a sheet only follows a
 * custom-scheme link on a tap, so a button is drawn as well as trying
 * automatically. Returns true when it took over the page.
 */
export const takeOverShellSsoReturn = ({
	pathname = window.location.pathname,
	search = window.location.search,
} = {}) => {
	if (pathname !== '/saml-auth/login' || isMobileShell()) return false;
	const params = new URLSearchParams(search);
	if (params.get('state') !== 'erinshell') return false;
	const result = params.get('code')
		? `code=${encodeURIComponent(params.get('code'))}`
		: `error=${encodeURIComponent(params.get('error') || 'access_denied')}`;
	const url = `erinapp://saml-auth/login?${result}`;
	const root = document.querySelector('#root') || document.body;
	root.innerHTML = '';
	const wrap = document.createElement('div');
	wrap.setAttribute(
		'style',
		'min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;font-family:-apple-system,system-ui,sans-serif'
	);
	const link = document.createElement('a');
	link.href = url;
	link.textContent = 'Continue to the ERIN app';
	link.setAttribute(
		'style',
		'display:inline-block;padding:14px 28px;border-radius:24px;background:#018dd3;color:#fff;font-size:16px;font-weight:600;text-decoration:none'
	);
	wrap.append(link);
	root.append(wrap);
	try {
		window.location.replace(url);
	} catch {
		// the link stays for a tap
	}
	return true;
};
